/**
 * deriveScreen.ts — Pure function mapping engine state + UI state to screen.
 *
 * UI-INV-004: deriveScreen() is a pure function with zero side effects.
 * This is the ONLY decision logic in the view layer.
 *
 * Priority order (first match wins):
 *   0. Booting (probe not done) → booting
 *   1. First launch → permissions
 *   2. Install nudge → install-bridge / install-prompt
 *   3. First-time user onboarding → onboarding
 *   4. Reflection (post-completion ceremony) → reflection
 *   4. Completion ceremony → completion
 *   5. Bell preview → bell-preview
 *   6. Bell library → bell-library
 *   7. Admin → admin
 *   7b. Feedback → feedback
 *   7c. What's New → whats-new
 *   8. Settings → settings
 *   9. History → history
 *   9. Missed scheduled retreat → missed (M2-UX-P1-015)
 *  10. Intention pending (before any retreat start) → intention
 *  11. Singleton gate (running + library nav) → singleton-gate
 *  12. Library flow → library / retreat-detail  ← before any "home" fallback
 *  13. Returning (auto-resume or other-tab) → home
 *  14. Running + wake-up container → wake-up-container (M2-UX-P1-013)
 *  15. Running + end-intention (Buddha moment) → end-intention
 *  16. Running + end confirm → end-confirm
 *  17. Running → live
 *  18. Initializing + preparationSeen + pendingSession → booting
 *  19. Initializing + preparationSeen → home
 *  20. Initializing → preparation
 *  21. Scheduling flow: selection → selection
 *  22. Scheduling flow: scheduling → scheduling
 *  23. Armed retreat → waiting (M2-UX-P1-012)
 *  24. Has scheduled retreat → home-scheduled
 *  25. Default → home
 */
import type { EngineSnapshot } from "@retreats/engine";
import type { AppScreen, UIState } from "./types";
import { RetreatState } from "./types";

export function deriveScreen(
  snapshot: EngineSnapshot,
  uiState: UIState,
): AppScreen {
  // M2-UX-P0-005: Boot state — probe() hasn't finished yet.
  // No screen is interactive during boot. Prevents race conditions.
  if (uiState.isBooting) {
    return "booting";
  }

  // First launch takes precedence over everything
  if (uiState.isFirstLaunch) {
    return "permissions";
  }

  // Install nudge — shown after permissions if app is not installed (M2-UX-P2-005)
  if (uiState.installFlow === "install-bridge") return "install-bridge";
  if (uiState.installFlow === "install-prompt") return "install-prompt";

  // First-time user onboarding — shown once after permissions/install until "Begin" is tapped
  if (!uiState.onboardingSeen) return "onboarding";

  // Reflection — shown on top of completion (both flags simultaneously true)
  if (uiState.showReflection) return "reflection";

  // Completion ceremony (post-stop, before returning to idle)
  if (uiState.showCompletion) {
    return "completion";
  }

  // Bell drill-down — settings sub-screens (deepest first)
  if (uiState.bellFlow === "bell-preview") return "bell-preview";
  if (uiState.bellFlow === "bell-library") return "bell-library";

  // Trust screen — sub-screen navigated from home-scheduled trust badge
  if (uiState.showTrust) return "trust";

  // Teaching screen — reachable from settings; shows bell meanings for active retreat
  if (uiState.showTeaching) return "teaching";

  // Settings / history / feedback / admin / schedule-editor overlays
  if (uiState.showAdmin)    return "admin";
  if (uiState.showFeedback) return "feedback";
  if (uiState.showWhatsNew) return "whats-new";
  if (uiState.showSettings) return "settings";
  if (uiState.showHistory)  return "history";
  if (uiState.showScheduleEditor) return "schedule-editor";

  // M2-UX-P1-015: Missed scheduled retreat — must clear before re-scheduling
  if (uiState.isMissed) {
    return "missed";
  }

  // Intention gate: fires before any retreat start. Cleared when user submits or declines.
  // Guard against stale flag if engine somehow recovered to RUNNING (e.g. crash during intention).
  // Scheduled retreats auto-decline intention — no gate needed, engine initializes immediately.
  if (
    uiState.intentionAction === 'now' &&
    snapshot.status !== RetreatState.RUNNING
  ) {
    return "intention";
  }

  // Singleton gate: retreat is running and user navigated to Library/Discover.
  // Must come before isReturning so it fires even when TabBar is visible (returning state).
  // Running sub-states (end-confirm, end-intention, wake-up-container) take priority.
  if (
    snapshot.status === RetreatState.RUNNING &&
    !uiState.showEndConfirm &&
    !uiState.showEndIntention &&
    !uiState.showWakeUpContainer &&
    (uiState.libraryFlow === "library" || uiState.libraryFlow === "retreat-detail")
  ) {
    return "singleton-gate";
  }

  // Other-tab conflict or same-tab pending session: show home with resume card.
  // Both cases mean "there is a retreat to continue". The card handles the distinction.
  if (uiState.isReturning || uiState.otherTabActive) {
    return "home";
  }

  // Running states — must come before library flow so end-confirm takes priority
  if (snapshot.status === RetreatState.RUNNING) {
    // Wake ritual — 3-frame monastic entry for scheduled retreats (takes priority over wake-up-container)
    if (uiState.showWakeRitual) {
      return "wake-ritual";
    }
    // M2-UX-P1-013: Wake-up container — first container of a scheduled retreat
    if (uiState.showWakeUpContainer) {
      return "wake-up-container";
    }
    if (uiState.showEndIntention) {
      return "end-intention";
    }
    if (uiState.showEndConfirm) {
      return "end-confirm";
    }
    return "live";
  }

  // Library flow — must be after RUNNING state checks so running sub-states take priority
  if (uiState.libraryFlow === "library") return "library";
  if (uiState.libraryFlow === "retreat-detail") return "retreat-detail";

  // Initializing → preparation screen (first retreat only — M2-UX-P1-002)
  // If preparation has been seen and there's no pending session, show home instead of booting
  if (snapshot.status === RetreatState.INITIALIZING) {
    if (uiState.preparationSeen && !snapshot.pendingSession) {
      return "home";
    }
    return uiState.preparationSeen ? "booting" : "preparation";
  }

  // ── Scheduling flow (M2-UX-P0-011) ────────────────────────
  if (uiState.schedulingFlow === "selection") {
    return "selection";
  }

  if (uiState.schedulingFlow === "scheduling") {
    return "scheduling";
  }

  if (uiState.schedulingFlow === "wizard") {
    return "schedule-wizard";
  }

  // M2-UX-P1-012: Armed state — retreat begins imminently
  if (uiState.hasScheduledRetreat && uiState.isArmed) {
    return "waiting";
  }

  // Has a scheduled retreat → show countdown home
  if (uiState.hasScheduledRetreat) {
    return "home-scheduled";
  }

  // Default: idle, no pending session → home
  return "home";
}
